import {
  AnyAction,
  createAsyncThunk,
  Dispatch,
  ThunkAction,
  ThunkDispatch
} from '@reduxjs/toolkit';
import axios from 'axios';
import { toast } from 'react-toastify';

import { HttpStatus } from 'constants/statusCodes';
import { getCookie, setCookie } from 'helpers/cookie';
import {
  getErrorStatus,
  handleRequestError,
  isNetworkError,
  type RequestError
} from 'helpers/network';
import { getTranslationKey } from 'helpers/texting';
import { SELECTION_SAVED_TOAST_ID } from 'helpers/selectionSavedToast';
import { getEndCustomer } from 'old-store/actions';
import type { RootState } from 'old-store/store';
import type { ISelection } from 'old-store/types/gallery';
import { Api } from 'old-store/utils';
import ApiErrors from 'old-store/utils/API/APIErrors';
import type { ICollection } from 'types/collection';
import {
  addLikes,
  addSelection,
  addSelectionEditImages,
  completeSelectionEdit,
  disableUpdateSelectionState,
  enableUpdateSelectionState,
  finishSelection,
  handleLikes,
  removeLikes,
  removeSelection,
  removeSelectionEditImages,
  replaceSelection,
  setPendingLoginSave,
  setSelectionEditStatus,
  startSelection,
  toggleSelectionEditImage
} from 'modules/ScrAppBookNew/store/selection';
import { createSelectableImageFilter } from 'modules/ScrAppBookNew/helpers/galleryImages';
import { hideSelectedResults } from 'modules/ScrAppBookNew/store/filters';
import { closeSelectionSaveUi, openSelectionLimitModal } from 'modules/ScrAppBookNew/store/ui';
import { setLoginFlowStatus } from 'modules/ScrShopNew/store/slices/shop';
import { startOtpLogin } from 'modules/ScrShopNew/store/slices/shop/actions';
import {
  toggleLoginModal,
  togglePasswordModal,
  toggleSelectionSentCongratsModal
} from 'store/slices/modals';

export interface ISelectionSaveValues {
  selectionName: string | null;
  message?: string | null;
  firstName: string;
  lastName: string;
  email: string;
  // the selection to submit; without it the running one is used
  selectionId?: string;
  withCongratsModal?: boolean;
  withPasswordModal?: boolean;
  successMessageKey?: string;
}

interface ILikeImageRequest {
  imageId: string;
  optimisticUpdate?: boolean;
}

const checkIfAuthorizedAndLogoutIfNot = (code: number, user = '') => {
  if (code !== HttpStatus.Forbidden && code !== HttpStatus.NotFound) return;

  if (user) {
    const stored = localStorage.getItem('endCustomerToken');
    const tokens = stored ? JSON.parse(stored) : {};
    localStorage.setItem('endCustomerToken', JSON.stringify({ ...tokens, [user]: undefined }));
    setCookie('endCustomerToken', '');
  }

  window.location.reload();
};

export const fetchCustomerToken = async (user: string) => {
  const actualCookie = getCookie('endCustomerToken');

  if (actualCookie) {
    const isValid = await axios
      .get('/api/end-customers/validate-token', {
        headers: { authorizationendcustomer: actualCookie },
        params: { collectionId: window.SITE_ID },
        validateStatus: (status) => status === 200 || status === 403
      })
      .then((response) => response.status === 200);

    if (isValid) {
      const stored = localStorage.getItem('endCustomerToken');
      const tokens = stored ? JSON.parse(stored) : {};
      localStorage.setItem('endCustomerToken', JSON.stringify({ ...tokens, [user]: actualCookie }));
    }
  }

  const stored = localStorage.getItem('endCustomerToken');
  const tokens = stored ? JSON.parse(stored) : {};
  const existingToken = tokens[user];

  if (existingToken) {
    setCookie('endCustomerToken', existingToken, 30);

    return existingToken;
  }

  const {
    result: { token, flyUserId }
  } = await Api.TempCustomer.createTempCustomer();

  localStorage.setItem('flyUserId', flyUserId);
  localStorage.setItem('endCustomerToken', JSON.stringify({ ...tokens, [user]: token }));
  setCookie('endCustomerToken', token, 30);

  return token;
};

export const getCustomerSelectionId = async (
  state: RootState,
  dispatch: Dispatch<AnyAction>
): Promise<string> => {
  const { collection } = state;
  const { selection } = state.scrAppBookNew;
  const currentSelection = selection.all.find((item) => item.current);

  if (currentSelection) return currentSelection._id;

  const createdSelection = await Api.TempCustomer.createSelection();
  ApiErrors.checkOnApiError(createdSelection);
  dispatch(addSelection({ ...createdSelection, current: true }));

  await Api.TempCustomer.setSelection({
    collection_id: collection._id || window.SITE_ID,
    selection_id: createdSelection._id
  });

  return createdSelection._id;
};

class LikeRequestError extends Error {
  code: number;

  constructor(message: string, code: number) {
    super(message);
    this.name = 'LikeRequestError';
    this.code = code;
  }
}

const unavailableLikeError = () =>
  new LikeRequestError(
    navigator.onLine ? 'Like request failed' : 'Network Error',
    navigator.onLine ? HttpStatus.InternalServerError : 0
  );

// The like endpoints answer 409 when a requested selection is already full. Our own counter can be
// stale (another tab filled it), so the server's count wins: show the limit window and re-read it.
const handleSelectionLimitError = (error: any, dispatch: any): boolean => {
  if (getErrorStatus(error) !== HttpStatus.Conflict) return false;

  dispatch(openSelectionLimitModal());
  dispatch(getEndCustomer());

  return true;
};

const checkLikeResponse = (response: any) => {
  if (isNetworkError(response)) {
    throw new LikeRequestError(response?.message || 'Network Error', response?.code || 0);
  }
  ApiErrors.checkOnApiError(response);
};

let likeRequestProcessing = false;
const likeRequestQueue: string[] = [];

export const likeImage = createAsyncThunk<void, ILikeImageRequest, { state: RootState }>(
  'scrAppBookNew/selection/likeImage',
  async ({ imageId, optimisticUpdate = true }, { getState, dispatch }) => {
    const state = getState();
    const { selection } = state.scrAppBookNew;
    const { collection, images, endCustomer } = state;

    if (optimisticUpdate && selection.editDraft) {
      const image = images.images.find((item) => item._id === imageId);
      if (!image || image.hiddenBy?.includes(endCustomer._id)) return;

      const source = selection.all.find(
        (item) => item._id === selection.editDraft?.sourceSelectionId
      );
      const addingImage = !selection.editDraft.imageIds.includes(imageId);
      const { maxLikes } = source || {};

      if (addingImage && maxLikes && selection.editDraft.imageIds.length >= maxLikes) {
        dispatch(openSelectionLimitModal());

        return;
      }

      dispatch(toggleSelectionEditImage(imageId));

      return;
    }

    if (optimisticUpdate) {
      const addingImage = !selection.likes.includes(imageId);
      const { maxLikes } = selection.currentSelection;
      const exceedsLimit =
        addingImage && !!maxLikes && maxLikes > 0 && selection.likes.length + 1 > maxLikes;

      if (exceedsLimit) {
        dispatch(openSelectionLimitModal());

        return;
      }

      dispatch(handleLikes(imageId));
    }

    if (likeRequestProcessing) {
      likeRequestQueue.push(imageId);

      return;
    }
    likeRequestProcessing = true;

    try {
      const token = await fetchCustomerToken(collection._user);
      if (!token) throw unavailableLikeError();

      const selectionId = await getCustomerSelectionId(getState(), dispatch);
      if (!selectionId) throw unavailableLikeError();

      const response = await Api.TempCustomer.like({
        collection_id: collection._id || window.SITE_ID,
        selection_id: selectionId,
        image_id: imageId
      });
      checkLikeResponse(response);
    } catch (error: any) {
      dispatch(handleLikes(imageId));

      if (!handleSelectionLimitError(error, dispatch)) {
        checkIfAuthorizedAndLogoutIfNot(error.code, collection._user);
        handleRequestError(error, { showFallback: true });
      }
    } finally {
      likeRequestProcessing = false;
      const nextImageId = likeRequestQueue.shift();
      if (nextImageId) {
        dispatch(likeImage({ imageId: nextImageId, optimisticUpdate: false }));
      }
    }
  }
);

export const commentImage = createAsyncThunk<
  any,
  { imageId: string; comment: string },
  { state: RootState }
>('scrAppBookNew/selection/commentImage', async ({ imageId, comment }, { getState, dispatch }) => {
  const { collection } = getState();

  try {
    await fetchCustomerToken(collection._user);
    const selectionId = await getCustomerSelectionId(getState(), dispatch);
    const response = await Api.TempCustomer.comment({
      image_id: imageId,
      selection_id: selectionId,
      collection_id: collection._id || window.SITE_ID,
      comment
    });
    ApiErrors.checkOnApiError(response);
    await (dispatch as any)(getEndCustomer());
    toast.success(getTranslationKey('commentSaved'), {
      className: 'toast-custom-top-position',
      toastId: `comment-saved-${imageId}`
    });

    return response.result;
  } catch (error: any) {
    checkIfAuthorizedAndLogoutIfNot(error.code, collection._user);
    handleRequestError(error, { showFallback: true });
    throw error;
  }
});

const bulkLike =
  (imageIds: string[], like: boolean): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    if (!imageIds.length) return;

    const initialSelection = getState().scrAppBookNew.selection;
    const sourceSelection = initialSelection.editDraft
      ? initialSelection.all.find(
          (item) => item._id === initialSelection.editDraft?.sourceSelectionId
        )
      : initialSelection.all.find((item) => item.current);
    let scopedImageIds = imageIds;

    if (like && sourceSelection?.maxLikes && sourceSelection.maxLikes > 0) {
      const newImageIds = imageIds.filter((id) => !initialSelection.likes.includes(id));
      const remaining = Math.max(sourceSelection.maxLikes - initialSelection.likes.length, 0);

      if (newImageIds.length > remaining) dispatch(openSelectionLimitModal());

      const allowed = new Set(newImageIds.slice(0, remaining));
      scopedImageIds = imageIds.filter(
        (id) => initialSelection.likes.includes(id) || allowed.has(id)
      );
    }

    if (!scopedImageIds.length) return;

    if (initialSelection.editDraft) {
      dispatch(
        like ? addSelectionEditImages(scopedImageIds) : removeSelectionEditImages(scopedImageIds)
      );

      return;
    }

    try {
      const state = getState();
      const { collection } = state;
      await fetchCustomerToken(collection._user);
      const selectionId = await getCustomerSelectionId(state, dispatch);
      const response = await Api.TempCustomer.likeBulk({
        collection_id: collection._id || window.SITE_ID,
        selection_id: selectionId,
        image_ids: scopedImageIds,
        like
      });

      checkLikeResponse(response);
      dispatch(like ? addLikes(scopedImageIds) : removeLikes(scopedImageIds));
    } catch (error: any) {
      if (handleSelectionLimitError(error, dispatch)) return;

      const { collection } = getState();
      checkIfAuthorizedAndLogoutIfNot(error.code, collection._user);
      handleRequestError(error, { showFallback: true });
    }
  };

export const selectAllImages =
  (visibleImageIds?: string[]): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
  (dispatch, getState) => {
    const state = getState();
    const { likes } = state.scrAppBookNew.selection;
    const isSelectable = createSelectableImageFilter({
      endCustomerId: state.endCustomer._id,
      visibleIds: visibleImageIds && new Set(visibleImageIds)
    });
    const alreadyLiked = new Set(likes);
    const imageIds = state.images.images
      .filter((image) => isSelectable(image) && !alreadyLiked.has(image._id))
      .map((image) => image._id);

    return dispatch(bulkLike(imageIds, true));
  };

export const deselectAllImages =
  (visibleImageIds?: string[]): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
  (dispatch, getState) => {
    const visible = visibleImageIds ? new Set(visibleImageIds) : null;
    const imageIds = getState().scrAppBookNew.selection.likes.filter(
      (imageId) => !visible || visible.has(imageId)
    );

    return dispatch(bulkLike(imageIds, false));
  };

const createFreshSelection = async (
  dispatch: ThunkDispatch<RootState, unknown, AnyAction>,
  getState: () => RootState
): Promise<ISelection | null> => {
  const { collection } = getState();
  if (collection.collectionCreator?.featureFlags?.newDesign !== true) return null;

  try {
    await fetchCustomerToken(collection._user);
    const createdSelection = await Api.TempCustomer.createSelection();
    ApiErrors.checkOnApiError(createdSelection);
    await Api.TempCustomer.setSelection({
      collection_id: collection._id,
      selection_id: createdSelection._id
    });

    dispatch(finishSelection());
    dispatch(addSelection({ ...createdSelection, current: true }));
    dispatch(startSelection());
    await (dispatch as any)(getEndCustomer());
    dispatch(startSelection());

    return createdSelection;
  } catch (error: any) {
    checkIfAuthorizedAndLogoutIfNot(error.code, collection._user);
    handleRequestError(error, { showFallback: true });

    return null;
  }
};

// a second click while the first request is still out would create a second empty selection
let freshSelectionRequest: Promise<ISelection | null> | null = null;

export const startFreshSelection =
  (): ThunkAction<Promise<ISelection | null>, RootState, unknown, AnyAction> =>
  (dispatch, getState) => {
    if (!freshSelectionRequest) {
      freshSelectionRequest = createFreshSelection(dispatch, getState).finally(() => {
        freshSelectionRequest = null;
      });
    }

    return freshSelectionRequest;
  };

export const cancelSelectionAndDeleteIfUnsaved = createAsyncThunk<void, void, { state: RootState }>(
  'scrAppBookNew/selection/cancel',
  async (_, { getState, dispatch }) => {
    const { collection } = getState();

    try {
      const currentSelection = getState().scrAppBookNew.selection.all.find((item) => item.current);
      // Selection mode can be on before anything is liked, so there may be no selection on the
      // server to unset or delete. That is not a failure — throwing here would reach the catch
      // below and pop the generic error toast on a plain discard.
      if (!currentSelection) return;

      // An unnamed selection is about to be deleted, so drop its section from the lists now: the
      // caller resets the rest of the selection state synchronously, and leaving this to the
      // refresh below made the page relayout a second after the discard. A failed request restores
      // it with the next refresh.
      const isUnsaved = !currentSelection.name;
      if (isUnsaved) dispatch(removeSelection(currentSelection._id));

      await fetchCustomerToken(collection._user);
      const response = await Api.TempCustomer.unsetSelection({
        collection_id: collection._id || window.SITE_ID,
        selection_id: currentSelection._id
      });
      ApiErrors.checkOnApiError(response);

      if (isUnsaved) {
        await Api.TempCustomer.deleteSelection(currentSelection._id);
      }

      dispatch(finishSelection());
      dispatch(hideSelectedResults());
      await (dispatch as any)(getEndCustomer());
    } catch (error: any) {
      checkIfAuthorizedAndLogoutIfNot(error.code, collection._user);
      handleRequestError(error, { showFallback: true });
    }
  }
);

export const setActiveSelection =
  (
    selectionId: string,
    collection: ICollection
  ): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      await fetchCustomerToken(collection._user);
      await Api.TempCustomer.setSelection({
        selection_id: selectionId,
        collection_id: collection._id
      });
      await (dispatch as any)(getEndCustomer());
    } catch (error: any) {
      handleRequestError(error, { showFallback: true });
    }
  };

const replaceSelectionLikes = async (
  collectionId: string,
  selectionId: string,
  currentImageIds: string[],
  nextImageIds: string[]
) => {
  const currentIds = new Set(currentImageIds);
  const nextIds = new Set(nextImageIds);
  const changes = [
    { like: true, imageIds: nextImageIds.filter((id) => !currentIds.has(id)) },
    { like: false, imageIds: currentImageIds.filter((id) => !nextIds.has(id)) }
  ].filter(({ imageIds }) => imageIds.length);

  await Promise.all(
    changes.map(async ({ like, imageIds }) => {
      const response = await Api.TempCustomer.likeBulk({
        collection_id: collectionId,
        selection_id: selectionId,
        image_ids: imageIds,
        like
      });
      checkLikeResponse(response);
    })
  );
};

const likedImageIds = (selection: ISelection) =>
  selection.favorites.filter((favorite) => favorite.like).map((favorite) => favorite._image);

// A purchase copy is unnamed, unsent and holds exactly the source's photos, so a later tap can
// reuse it instead of creating another one.
const findPurchaseCopy = (selections: ISelection[], source: ISelection) => {
  const sourceIds = new Set(likedImageIds(source));

  return selections.find((candidate) => {
    if (candidate._id === source._id || candidate.completed || candidate.name) return false;

    const candidateIds = likedImageIds(candidate);

    return candidateIds.length === sourceIds.size && candidateIds.every((id) => sourceIds.has(id));
  });
};

// The upsell modal buys the running selection's images, so a saved one becomes the running one
// first. The server only does that for an unsent selection whose _endCustomer is this customer —
// _savedByEndCustomer doesn't count — so any other one goes on as a copy of its images. An unnamed
// running selection becomes that copy, as switching away from it would leave a second «Current
// selection» nobody can delete.
export const activateSelectionForPurchase = createAsyncThunk<
  boolean,
  ISelection,
  { state: RootState }
>('scrAppBookNew/selection/activateForPurchase', async (selection, { dispatch, getState }) => {
  const { collection, scrAppBookNew, endCustomer } = getState();
  const asCopy = !!selection.completed || selection._endCustomer !== endCustomer?._id;

  try {
    await fetchCustomerToken(collection._user);
    let selectionId = selection._id;

    if (asCopy) {
      const copy = findPurchaseCopy(scrAppBookNew.selection.all, selection);

      if (copy?.current) return true;

      const running = scrAppBookNew.selection.all.find(
        (item) => item.current && !item.name && !item.completed
      );

      if (running) {
        await replaceSelectionLikes(
          collection._id,
          running._id,
          likedImageIds(running),
          likedImageIds(selection)
        );
        selectionId = running._id;
      } else if (copy) {
        selectionId = copy._id;
      } else {
        const created = await Api.TempCustomer.createSelectionFromImages({
          collection_id: collection._id,
          image_ids: likedImageIds(selection)
        });
        ApiErrors.checkOnApiError(created);
        selectionId = created._id;
      }
    }

    await Api.TempCustomer.setSelection({
      selection_id: selectionId,
      collection_id: collection._id
    });
    await (dispatch as any)(getEndCustomer());

    return true;
  } catch (error: any) {
    checkIfAuthorizedAndLogoutIfNot(error.code, collection._user);
    handleRequestError(error, { showFallback: true });

    return false;
  }
});

// Edit-mode picks only live in the draft until they are written to the server here.
const applyEditDraftLikes = (
  collectionId: string,
  editDraft: NonNullable<RootState['scrAppBookNew']['selection']['editDraft']>
) =>
  replaceSelectionLikes(
    collectionId,
    editDraft.sourceSelectionId,
    editDraft.originalImageIds,
    editDraft.imageIds
  );

// an explicit id lets the send form submit a saved selection instead of the running one
const resolveSelectionId = (state: RootState, values: ISelectionSaveValues) =>
  values.selectionId ||
  state.scrAppBookNew.ui.sendSelectionModal.selectionId ||
  state.scrAppBookNew.selection.all.find((item) => item.current)?._id;

const persistSelection = async (
  state: RootState,
  dispatch: Dispatch<AnyAction>,
  values: ISelectionSaveValues
) => {
  const { selection } = state.scrAppBookNew;
  const selectionId = resolveSelectionId(state, values);
  const request = {
    collection_id: state.collection._id || window.SITE_ID,
    selection_id: selectionId,
    name: values.selectionName,
    firstName: values.firstName,
    lastName: values.lastName,
    email: values.email,
    message: values.message
  };

  await fetchCustomerToken(state.collection._user);

  // sending a selection mid-edit must submit the edited images, not the stored ones
  if (selection.editDraft && selection.editDraft.sourceSelectionId === selectionId) {
    await applyEditDraftLikes(request.collection_id, selection.editDraft);
  }

  const saved = selection.updateSelectionState
    ? await Api.Selection.updateSelection(request)
    : await Api.Selection.saveSelection(request);
  ApiErrors.checkOnApiError(saved);

  // A guest who saves with a new email stays a guest: the selection goes into an account created
  // for that email, but getting into it takes a normal sign-in (one-time code or password).
  const unsetResponse = await Api.TempCustomer.unsetSelection({
    collection_id: request.collection_id,
    selection_id: selectionId
  });
  ApiErrors.checkOnApiError(unsetResponse);
  await (dispatch as any)(getEndCustomer());

  return saved;
};

// The BE answers 429 when a not-yet-registered customer saves selections with more than 5
// different email addresses within an hour.
export const SELECTION_EMAIL_LIMIT_ERROR = 'selection-email-limit';

export const SELECTION_LOGIN_REQUIRED_ERROR = 'selection-login-required';

const isLoginRequired = (error: RequestError) =>
  error.code === HttpStatus.Conflict && (error.data as any)?.error === 'LOGIN_REQUIRED';

// The email belongs to an account, which only its owner may save into. The login opens above the
// form; the code login resumes the save once signed in, the password login re-saves itself.
const askToLogInBeforeSaving = (
  dispatch: ThunkDispatch<RootState, unknown, AnyAction>,
  state: RootState,
  values: ISelectionSaveValues
) => {
  if (state.collection.collectionCreator?.featureFlags?.newShop !== true) {
    dispatch(toggleLoginModal());

    return;
  }

  dispatch(
    setPendingLoginSave({
      values: { ...values, selectionId: resolveSelectionId(state, values) },
      update: state.scrAppBookNew.selection.updateSelectionState
    })
  );
  dispatch(setLoginFlowStatus({ showLoginWithPasswordModal: true }));
  dispatch(startOtpLogin(values.email));
};

export const saveSelection = createAsyncThunk<
  any,
  ISelectionSaveValues,
  { state: RootState; rejectValue: string }
>('scrAppBookNew/selection/save', async (values, { getState, dispatch, rejectWithValue }) => {
  try {
    const savedSelection = await persistSelection(getState(), dispatch, values);
    if (!savedSelection) return rejectWithValue('selection-save-failed');

    dispatch(closeSelectionSaveUi());
    dispatch(finishSelection());
    dispatch(hideSelectedResults());

    if (values.withPasswordModal) dispatch(togglePasswordModal());
    else if (values.withCongratsModal) dispatch(toggleSelectionSentCongratsModal());
    else {
      toast.success(getTranslationKey(values.successMessageKey || 'selectionSaved'), {
        theme: 'colored',
        toastId: SELECTION_SAVED_TOAST_ID
      });
    }

    return { currentSelection: savedSelection };
  } catch (error: any) {
    const requestError = handleRequestError(error);

    // the form shows this under the email field instead of failing silently
    if (requestError.code === 429) return rejectWithValue(SELECTION_EMAIL_LIMIT_ERROR);

    if (isLoginRequired(requestError)) {
      askToLogInBeforeSaving(dispatch, getState(), values);

      return rejectWithValue(SELECTION_LOGIN_REQUIRED_ERROR);
    }

    // a parallel save just created the account for this email; the generic error invites a retry
    if (requestError.code === HttpStatus.Conflict) {
      handleRequestError(error, { showFallback: true });

      return rejectWithValue(requestError.message);
    }

    checkIfAuthorizedAndLogoutIfNot(requestError.code, getState().collection._user);

    return rejectWithValue(requestError.message);
  }
});

// the save stays pending until its request settles, so the form keeps its spinner meanwhile
export const resumeSelectionSaveAfterLogin =
  (): ThunkAction<Promise<void>, RootState, unknown, AnyAction> => async (dispatch, getState) => {
    const pending = getState().scrAppBookNew.selection.pendingLoginSave;
    if (!pending) return;

    dispatch(pending.update ? enableUpdateSelectionState() : disableUpdateSelectionState());
    await dispatch(saveSelection(pending.values));
    dispatch(setPendingLoginSave(null));
  };

export const SELECTION_CLOSED_ERROR = 'selection-closed';

export const commitSelectionEdits = createAsyncThunk<
  ISelection,
  // `name` is only read for mode 'new' — an overwrite keeps the selection's existing name.
  { mode: 'overwrite' | 'new'; name?: string },
  { state: RootState; rejectValue: string }
>(
  'scrAppBookNew/selection/commitEdits',
  async ({ mode, name }, { dispatch, getState, rejectWithValue }) => {
    const { collection, endCustomer } = getState();
    const startDraft = getState().scrAppBookNew.selection.editDraft;

    if (!startDraft) return rejectWithValue('invalid-selection-edit');

    dispatch(setSelectionEditStatus('saving'));

    // Another device may have sent this selection since the edit began; local state can't tell.
    if (mode === 'overwrite') await (dispatch as any)(getEndCustomer());

    const { selection } = getState().scrAppBookNew;
    const { editDraft } = selection;
    const source = selection.all.find((item) => item._id === editDraft?.sourceSelectionId);

    if (!editDraft || !source) {
      dispatch(setSelectionEditStatus('failed'));

      return rejectWithValue('invalid-selection-edit');
    }

    if (mode === 'overwrite' && source.completed) {
      dispatch(setSelectionEditStatus('closed'));

      return rejectWithValue(SELECTION_CLOSED_ERROR);
    }
    let createdSelectionId: string | null = null;

    try {
      await fetchCustomerToken(collection._user);
      let savedSelection: ISelection;

      if (mode === 'overwrite') {
        await applyEditDraftLikes(collection._id, editDraft);

        savedSelection = await Api.Selection.updateSelection({
          collection_id: collection._id,
          selection_id: editDraft.sourceSelectionId,
          name: source.name,
          // the update endpoint rejects null names with a 422; older accounts may have none
          firstName: endCustomer.firstName || '',
          lastName: endCustomer.lastName || '',
          email: endCustomer.email
        });
      } else {
        const editableSourceIds = new Set(editDraft.originalImageIds);
        const retainedIds = source.favorites
          .filter((favorite) => favorite.like && !editableSourceIds.has(favorite._image))
          .map((favorite) => favorite._image);
        const imageIds = Array.from(new Set([...editDraft.imageIds, ...retainedIds]));
        const created = await Api.TempCustomer.createSelectionFromImages({
          collection_id: collection._id,
          image_ids: imageIds
        });
        ApiErrors.checkOnApiError(created);
        createdSelectionId = created._id;

        await Promise.all(
          source.favorites
            .filter((favorite) => !!favorite.comment && imageIds.includes(favorite._image))
            .map(async (favorite) => {
              const response = await Api.TempCustomer.comment({
                collection_id: collection._id,
                selection_id: created._id,
                image_id: favorite._image,
                comment: favorite.comment
              });
              ApiErrors.checkOnApiError(response);
            })
        );

        savedSelection = await Api.Selection.updateSelection({
          collection_id: collection._id,
          selection_id: created._id,
          name: name?.trim() || source.name,
          firstName: endCustomer.firstName || '',
          lastName: endCustomer.lastName || '',
          email: endCustomer.email
        });
        createdSelectionId = null;
      }

      ApiErrors.checkOnApiError(savedSelection);
      dispatch(replaceSelection(savedSelection));
      dispatch(completeSelectionEdit(savedSelection));
      await (dispatch as any)(getEndCustomer());

      toast.success(getTranslationKey('selections.saveSelectionSheet.saveSuccess'), {
        theme: 'colored',
        toastId: SELECTION_SAVED_TOAST_ID
      });

      return savedSelection;
    } catch (error: any) {
      if (createdSelectionId) {
        try {
          await Api.TempCustomer.deleteSelection(createdSelectionId);
        } catch {
          // The original error is more useful than cleanup failure.
        }
      }
      dispatch(setSelectionEditStatus('failed'));
      checkIfAuthorizedAndLogoutIfNot(error.code, collection._user);

      return rejectWithValue(error?.message || 'selection-edit-failed');
    }
  }
);

// Selection sharing now goes through the "what do you want to share?" form (ShareCopyFlow), which
// owns the link request and the clipboard copy — hence no native share sheet here any more.
